/**
 * Creates a session cookie by exchanging an OAuth access token for a session.
 * This is used to enable iframe-based authentication in Office add-ins.
 *
 * @param accessToken - The OAuth access token
 * @param walterHost - The Walter application host URL
 * @returns Response from the session creation endpoint
 * @throws Error if the session creation fails
 */
export async function createSessionFromToken(
  accessToken: string,
  walterHost: string
): Promise<Response> {
  const response = await fetch(`${walterHost}/api/sessions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    mode: "cors",
    credentials: "include", // Critical: ensures cookies are saved and sent
  });

  if (!response.ok) {
    let errorBody = "";
    let errorMessage = `Failed to create session: ${response.status} ${response.statusText}`;

    // Handle 401 specifically - token is invalid or expired
    if (response.status === 401) {
      errorMessage = "Your session has expired. Please log out and log back in.";
    }

    try {
      errorBody = await response.text();
      console.error("Session creation error body:", errorBody);

      // Parse common error responses
      try {
        const errorJson = JSON.parse(errorBody);
        if (errorJson.error === "Insufficient scope") {
          // Show detailed message only in development
          if (import.meta.env.DEV) {
            errorMessage = "Your OAuth token is missing the 'web_embed' scope. Please log out and log back in. If the issue persists, run: bin/rails word_addin:setup";
          } else {
            errorMessage = "Please log out and log back in to refresh your session.";
          }
        } else if (errorJson.error === "Unauthorized") {
          errorMessage = "Your session has expired. Please log out and log back in.";
        } else if (errorJson.error) {
          errorMessage = import.meta.env.DEV
            ? `Authentication error: ${errorJson.error}`
            : "Authentication failed. Please try logging in again.";
        }
      } catch {
        // Not JSON, use raw body
        if (import.meta.env.DEV && errorBody) {
          errorMessage += `. ${errorBody}`;
        }
      }
    } catch (e) {
      console.error("Could not read response body:", e);
    }

    throw new Error(errorMessage);
  }

  return response;
}

/**
 * Clears Walter session cookies by calling the destroy endpoint.
 */
export async function clearWalterCookies(walterHost: string): Promise<void> {
  try {
    await fetch(`${walterHost}/api/sessions`, {
      method: "DELETE",
      mode: "cors",
      credentials: "include",
    });
  } catch (error) {
    console.error("Failed to clear cookies:", error);
    throw error;
  }
}
